import {
  PackageHeldBy,
  PackageHeldByList,
  PackageVisibility,
  PACKAGE_VISIBILITY_LABEL,
} from '@/types/package';

/** Publik first: it is the default and what almost every package is. */
export const PACKAGE_VISIBILITY_OPTIONS = Object.values(PackageVisibility).map(
  (visibility) => ({
    value: visibility,
    label: PACKAGE_VISIBILITY_LABEL[visibility],
  })
);

/** Fleet's archive-refusal rule: not archived, not ended (null never ends). */
export const liveHolders = (
  heldBy: PackageHeldBy[],
  now: Date = new Date()
): PackageHeldBy[] =>
  heldBy.filter(
    (holder) =>
      holder.status !== 'archived' &&
      (holder.endAt === null ||
        new Date(holder.endAt).getTime() > now.getTime())
  );

/** Missing `heldBy` is unknown, never free: free is a claim ops act on. */
export type HeldByCell =
  | { kind: 'holders'; holders: PackageHeldBy[] }
  | { kind: 'free' }
  | { kind: 'unknown' }
  | { kind: 'none' };

export const toHeldByCell = (
  heldBy: PackageHeldByList,
  visibility: PackageVisibility,
  now: Date = new Date()
): HeldByCell => {
  if (visibility !== PackageVisibility.PRIVATE) return { kind: 'none' };
  if (!heldBy) return { kind: 'unknown' };
  const holders = liveHolders(heldBy, now);
  return holders.length ? { kind: 'holders', holders } : { kind: 'free' };
};

export const HELD_BY_UNKNOWN_LABEL = 'Tidak bisa dicek';

/** One term for a free Khusus package, on the list and the package page. */
export const HELD_BY_FREE_LABEL = 'Belum dipakai campaign';
export const HELD_BY_FREE_HINT =
  'Belum dipakai campaign mana pun, jadi bisa dipilih di On Demand.';

export type VisibilityTab = 'ALL' | PackageVisibility;

/** `null` = that count failed to load; Semua then shows no number either. */
export const visibilityTabs = (
  counts: Record<PackageVisibility, number | null>
): { key: VisibilityTab; label: string; count: number | null }[] => {
  const { PUBLIC, PRIVATE } = counts;
  return [
    {
      key: 'ALL',
      label: 'Semua',
      count: PUBLIC === null || PRIVATE === null ? null : PUBLIC + PRIVATE,
    },
    ...PACKAGE_VISIBILITY_OPTIONS.map((option) => ({
      key: option.value,
      label: option.label,
      count: counts[option.value],
    })),
  ];
};

export type CampaignStatusLabel =
  'Draf' | 'Terjadwal' | 'Aktif' | 'Selesai' | 'Dijeda' | 'Diarsipkan';

const PLAIN_STATUS: Record<PackageHeldBy['status'], CampaignStatusLabel> = {
  draft: 'Draf',
  active: 'Aktif',
  paused: 'Dijeda',
  archived: 'Diarsipkan',
};

/** Same derivation as basecamp's campaign list, so both screens agree. */
export const holderStatusLabel = (
  holder: PackageHeldBy,
  now: Date = new Date()
): CampaignStatusLabel => {
  if (holder.status !== 'active') return PLAIN_STATUS[holder.status];
  const start = new Date(holder.startAt).getTime();
  const end = holder.endAt ? new Date(holder.endAt).getTime() : Infinity;
  if (Number.isNaN(start) || Number.isNaN(end)) return 'Aktif';
  if (now.getTime() < start) return 'Terjadwal';
  if (!(now.getTime() < end)) return 'Selesai';
  return 'Aktif';
};

/** "Riwayat campaign": everything `liveHolders` drops. */
export const pastHolders = (
  heldBy: PackageHeldBy[],
  now: Date = new Date()
): PackageHeldBy[] => {
  const live = new Set(liveHolders(heldBy, now));
  return heldBy.filter((holder) => !live.has(holder));
};

const WIB_DATE = new Intl.DateTimeFormat('id-ID', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'Asia/Jakarta',
});

const formatWibDate = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '-' : WIB_DATE.format(date);
};

/** "01 Nov – 31 Des 2026"; the start keeps its year only across years. */
export const formatHolderWindow = (holder: PackageHeldBy): string => {
  const start = formatWibDate(holder.startAt);
  if (holder.endAt === null) return `${start} – tanpa akhir`;
  const end = formatWibDate(holder.endAt);
  const year = /\d{4}$/;
  const sameYear = year.test(start) && start.slice(-4) === end.slice(-4);
  return `${sameYear ? start.slice(0, -5) : start} – ${end}`;
};

const referralPart = (holder: PackageHeldBy): string[] =>
  holder.referralCount === undefined
    ? []
    : [`${holder.referralCount} referral`];

export const formatHolderMeta = (holder: PackageHeldBy): string =>
  [
    'On Demand System',
    formatHolderWindow(holder),
    ...referralPart(holder),
  ].join(' · ');

export const formatHolderLine = (
  holder: PackageHeldBy,
  now: Date = new Date()
): string =>
  [
    holder.campaignName,
    formatHolderWindow(holder),
    holderStatusLabel(holder, now),
    ...referralPart(holder),
  ].join(' · ');

/** "Aktif, s.d. 31 Des 2026" for the archive refusal. */
export const formatHolderStanding = (
  holder: PackageHeldBy,
  now: Date = new Date()
): string =>
  `${holderStatusLabel(holder, now)}, ${
    holder.endAt === null
      ? 'tanpa akhir'
      : `s.d. ${formatWibDate(holder.endAt)}`
  }`;

/**
 * The list's campaign line under a Khusus chip: the live campaign's name
 * (`+N` past the first), "Belum dipakai campaign", or "Tidak bisa dicek".
 * Null for Publik, which no campaign can hold. `title` is the hover text.
 */
export type CampaignLineView = {
  kind: 'holders' | 'free' | 'unknown';
  text: string;
  more: number;
  title: string;
};

export const campaignLine = (
  heldBy: PackageHeldByList,
  visibility: PackageVisibility,
  now: Date = new Date()
): CampaignLineView | null => {
  const cell = toHeldByCell(heldBy, visibility, now);
  switch (cell.kind) {
    case 'none':
      return null;
    case 'unknown':
      return {
        kind: 'unknown',
        text: HELD_BY_UNKNOWN_LABEL,
        more: 0,
        title:
          'On Demand tidak menjawab; pemakaian package oleh campaign tidak bisa dicek saat ini.',
      };
    case 'free':
      return {
        kind: 'free',
        text: HELD_BY_FREE_LABEL,
        more: 0,
        title: HELD_BY_FREE_HINT,
      };
    case 'holders':
      return {
        kind: 'holders',
        text: cell.holders[0].campaignName,
        more: cell.holders.length - 1,
        title: cell.holders.map((h) => formatHolderLine(h, now)).join('\n'),
      };
  }
};

/** `null` when the basecamp URL is not configured: hide the link. */
export const campaignUrl = (
  campaignId: string,
  base: string | undefined = process.env.REACT_APP_BASECAMP_URL
): string | null =>
  base
    ? `${base.replace(/\/+$/, '')}/coupons/campaigns/${encodeURIComponent(campaignId)}`
    : null;
